import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core';

import { Modal, ModalContent, ModalFooter } from 'Components/Modal';
import { Text, Flex, SvgIcon } from 'Components/Base';
import { useConfig } from 'Context/ConfigContext';
import Loading from 'Components/Loading';

import PersonalizedDiscount from '../PersonalizedDiscount';

const SocialButton = styled((props) => (
  <Flex justifyContent="center" alignItems="center" bg="notiGrey.grey3" size="44px" {...props} />
))`
  border-radius: 12px;
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) => theme.colors.notiGrey.grey2};
  }
`;

const GraduateModal = ({
  imageUrl,
  title,
  courseTitle,
  isActive,
  isTooltipOpen,
  isGeneratingPersonalizedDiscountCodeLoading,
  isGeneratingPersonalizedDiscountCodeError,
  shouldShowPersonalizedDiscountSection,
  personalizedDiscount,
  onClose,
  onFacebookShare,
  onTwitterShare,
  onLinkedinShare,
  onCopyButtonClick,
}) => {
  const { t } = useTranslation('classroom');
  const { enablePersonalizedDiscount } = useConfig();
  let description = t('GRADUATE_MODAL.SHORT_DESCRIPTION');
  if (courseTitle) {
    description = t('GRADUATE_MODAL.DESCRIPTION', { title: courseTitle });
  }
  const isLoading = isGeneratingPersonalizedDiscountCodeLoading && !personalizedDiscount;

  return (
    <Modal
      disableTopOffset={isLoading}
      muiProps={{
        isActive,
        modalPaperStyle: { width: isLoading ? 'auto' : '100%' },
      }}
      contents={
        isLoading ? (
          <Flex justifyContent={'center'} alignItems={'center'}>
            <Loading width={100} height={100} />
          </Flex>
        ) : (
          <Flex flexDirection={'column'} alignItems={'center'} mb={7}>
            <ModalContent imageUrl={imageUrl} title={title} description={description} />
            {enablePersonalizedDiscount && shouldShowPersonalizedDiscountSection && (
              <PersonalizedDiscount
                isTooltipOpen={isTooltipOpen}
                isError={!personalizedDiscount}
                discountDescription={{
                  discount: personalizedDiscount?.discount,
                  currency: personalizedDiscount?.currency,
                  discountUnit: personalizedDiscount?.discountUnit,
                }}
                discountCode={personalizedDiscount?.code}
                discountExpiredAt={personalizedDiscount?.expiredAt}
                onCopyButtonClick={onCopyButtonClick}
              />
            )}
            {courseTitle && (
              <>
                <Text as="p" color={'greyscale.grey3'} mb={3} mt={7} textAlign={'center'}>
                  {t('GRADUATE_MODAL.SOCIAL_MEDIA.TITLE')}
                </Text>

                <Flex width="200px" justifyContent="space-between">
                  <SocialButton onClick={onFacebookShare}>
                    <SvgIcon
                      icon={SvgIcon.ICON_LIST.FACEBOOK_SQ}
                      size={21}
                      color="socialMedia.facebook"
                    />
                  </SocialButton>
                  <SocialButton onClick={onTwitterShare}>
                    <SvgIcon
                      icon={SvgIcon.ICON_LIST.TWITTER_BIRD}
                      size={21}
                      color="socialMedia.twitter"
                    />
                  </SocialButton>
                  <SocialButton onClick={onLinkedinShare}>
                    <SvgIcon
                      icon={SvgIcon.ICON_LIST.LINKEDIN}
                      size={21}
                      color="socialMedia.linkedin"
                    />
                  </SocialButton>
                </Flex>
              </>
            )}
          </Flex>
        )
      }
      footer={
        !isLoading && (
          <ModalFooter
            justifyContent="center"
            secondaryButtonLabel={'ปิดหน้าต่างนี้'}
            onSecondaryButtonClick={onClose}
          />
        )
      }
    />
  );
};

GraduateModal.propTypes = {
  imageUrl: PropTypes.string,
  title: PropTypes.string,
  courseTitle: PropTypes.string,
  isActive: PropTypes.bool.isRequired,
  isTooltipOpen: PropTypes.bool.isRequired,
  isGeneratingPersonalizedDiscountCodeLoading: PropTypes.bool.isRequired,
  isGeneratingPersonalizedDiscountCodeError: PropTypes.bool.isRequired,
  shouldShowPersonalizedDiscountSection: PropTypes.bool.isRequired,
  personalizedDiscount: PropTypes.shape({
    code: PropTypes.string,
    expiredAt: PropTypes.string,
    discount: PropTypes.number,
    currency: PropTypes.string,
    discountUnit: PropTypes.string,
  }),
  onClose: PropTypes.func,
  onFacebookShare: PropTypes.func,
  onTwitterShare: PropTypes.func,
  onLinkedinShare: PropTypes.func,
  onCopyButtonClick: PropTypes.func,
};

GraduateModal.defaultProps = {
  title: 'Congratulations!',
  onClose: () => {},
  onFacebookShare: () => {},
  onTwitterShare: () => {},
  onLinkedinShare: () => {},
  onCopyButtonClick: () => {},
};

export default GraduateModal;
